/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import {
	type InvitedAppShortcutAllowedApsKey,
	type InvitedAppShortcutAllowedSourceAppKey,
	type InvitedAppShortcutAllowedTargetAppKey,
} from '../../../../common/types';
import {
	getAppKeyByApsKey,
	isAllowedApsKey,
} from '../../../../common/utils/invited-app-shortcuts-utils';
import { InvitedAppShortcutsContext } from '../../../../controllers/invited-app-shortcuts-context';
import type { UseInvitedAppShortcutsResult } from '../../../../services/invited-app-shortcuts';
import { requestInvitedAppShortcutsCacheRefresh } from '../../../../services/invited-app-shortcuts/force-refresh-cache';
import { useInvitedAppAnalytics } from '../../../../services/invited-app-shortcuts/invited-app-analytics';
import { useInvitedAppCollaboratorsLazyPrefetch } from '../../../../services/invited-app-shortcuts/invited-app-shortcuts-collaborators';

import { InvitedAppShortcutLabel } from './invited-app-shortcut-label';
import { LazyInvitedAppPopup } from './lazy-invited-app-popup';
import type { CollaboratorsSource } from './lazy-invited-app-popup/types';

/**
 * Productionisation-mode counterpart of `InvitedAppShortcutsContainerExperiment`.
 * Differs in 3 places: collaborators come from the shared lazy provider
 * (`kind: 'lazy'`), hover debounce-fires a singleton `prefetchAll()`, and
 * `impressedProducts` is always `false` (GRS resolves impressions).
 * Kept as a strict duplicate of the experiment inner so that path stays
 * untouched.
 */
export interface InvitedAppShortcutsContainerProdInnerProps {
	children: (renderProps: { referrableShortcuts: ShortcutsConfig }) => React.ReactNode;
	cloudId: string;
	excludeLastFromRefs?: number;
	hoverDelayTimeMs?: number;
	prefetchDebounceMs?: number;
	resolverResult: UseInvitedAppShortcutsResult;
	shortcutsConfig: ShortcutsConfig;
	sourceApp: InvitedAppShortcutAllowedSourceAppKey;
}

const DEFAULT_HOVER_DELAY_MS = 300;
const DEFAULT_PREFETCH_DEBOUNCE_MS = 100;

export const InvitedAppShortcutsContainerProdInner = ({
	shortcutsConfig,
	resolverResult,
	hoverDelayTimeMs = DEFAULT_HOVER_DELAY_MS,
	prefetchDebounceMs = DEFAULT_PREFETCH_DEBOUNCE_MS,
	excludeLastFromRefs = 0,
	children,
	cloudId,
	sourceApp,
}: InvitedAppShortcutsContainerProdInnerProps): JSX.Element => {
	const {
		fireViewedInvitedAppShortcuts,
		fireInvitedAppShortcutsCacheRefreshSucceeded,
		fireInvitedAppShortcutsCacheRefreshFailed,
	} = useInvitedAppAnalytics();
	const prefetchAll = useInvitedAppCollaboratorsLazyPrefetch();

	const [isPopupShown, setIsPopupShown] = useState(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);

	const jiraShortcutRef = useRef<HTMLAnchorElement | null>(null);
	const confluenceShortcutRef = useRef<HTMLAnchorElement | null>(null);

	const unbindFunctionsRef = useRef<
		Map<React.MutableRefObject<HTMLAnchorElement | null>, UnbindFn>
	>(new Map());

	const [currentShortcutRef, setCurrentShortcutRef] =
		useState<React.MutableRefObject<HTMLAnchorElement | null>>();

	const shortcutRefs: Record<
		InvitedAppShortcutAllowedApsKey,
		React.MutableRefObject<HTMLAnchorElement | null>
	> = useMemo(
		() => ({
			JIRA_SOFTWARE: jiraShortcutRef,
			CONFLUENCE: confluenceShortcutRef,
		}),
		[],
	);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const hidePopup = useCallback(() => {
		setIsPopupShown(false);
		setCurrentShortcutRef(undefined);
	}, []);

	const showPopup = useCallback(() => {
		setIsPopupShown(true);
	}, []);

	const startClosePopupTimer = useCallback(() => {
		clearClosePopupTimer();
		closePopupTimerRef.current = setTimeout(hidePopup, hoverDelayTimeMs);
	}, [clearClosePopupTimer, hidePopup, hoverDelayTimeMs]);

	// Debounced singleton prefetch — quick cursor sweeps don't fire the
	// request; an already in-flight HTTP request is never cancelled.
	const debouncedPrefetchAll = useMemo(
		() => debounce(() => prefetchAll(), prefetchDebounceMs, { leading: false, trailing: true }),
		[prefetchAll, prefetchDebounceMs],
	);

	useEffect(() => () => debouncedPrefetchAll.cancel(), [debouncedPrefetchAll]);

	const handleMouseOverWithRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) => () => {
			clearClosePopupTimer();
			setCurrentShortcutRef(targetRef);
			showPopup();
			debouncedPrefetchAll();
		},
		[clearClosePopupTimer, showPopup, debouncedPrefetchAll],
	);

	// Direct handler (no factory) — it doesn't capture per-ref state, so a
	// single stable function can be re-used across both `mouseleave` and
	// `blur` bindings without allocating duplicates per ref.
	const handleMouseLeave = useCallback(() => {
		debouncedPrefetchAll.cancel();
		startClosePopupTimer();
	}, [startClosePopupTimer, debouncedPrefetchAll]);

	const setShortcutRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) =>
			(node: HTMLAnchorElement | null) => {
				const existing = unbindFunctionsRef.current.get(targetRef);
				if (existing) {
					existing();
					unbindFunctionsRef.current.delete(targetRef);
				}

				// oxlint-disable-next-line no-param-reassign
				targetRef.current = node;

				if (node) {
					const mouseoverListener = handleMouseOverWithRef(targetRef);
					const focusListener = handleMouseOverWithRef(targetRef);

					const unbind = bindAll(node, [
						{ type: 'mouseover', listener: mouseoverListener },
						{ type: 'focus', listener: focusListener },
						{ type: 'mouseleave', listener: handleMouseLeave },
						{ type: 'blur', listener: handleMouseLeave },
					]);

					unbindFunctionsRef.current.set(targetRef, unbind);
				}
			},
		[handleMouseOverWithRef, handleMouseLeave],
	);

	const handleOnMouseEnterPopup = useCallback(() => {
		clearClosePopupTimer();

		if (currentShortcutRef?.current) {
			const { parentElement } = currentShortcutRef.current;
			if (parentElement) {
				parentElement.style.backgroundColor = token('elevation.surface.hovered');
			}
		}

		showPopup();
	}, [clearClosePopupTimer, showPopup, currentShortcutRef]);

	const handleOnMouseLeavePopup = useCallback(() => {
		if (currentShortcutRef?.current) {
			const { parentElement } = currentShortcutRef.current;
			if (parentElement) {
				parentElement.style.backgroundColor = '';
			}
		}
		startClosePopupTimer();
	}, [currentShortcutRef, startClosePopupTimer]);

	// Warms GRS to the static variant for the next SSR load. Fire-and-forget, so
	// nothing here awaits it — the outcome only goes to analytics, because a failed
	// refresh is what leaves the animation replaying until the cache TTL expires.
	const handleAnimatedImpressed = useCallback(
		(targetApp: InvitedAppShortcutAllowedTargetAppKey) => {
			requestInvitedAppShortcutsCacheRefresh({
				cloudId,
				sourceApp,
				targetApp,
				onSucceeded: () => fireInvitedAppShortcutsCacheRefreshSucceeded({ targetApp }),
				onFailed: ({ error, errorStack }) =>
					fireInvitedAppShortcutsCacheRefreshFailed({ targetApp, error, errorStack }),
			});
		},
		[
			cloudId,
			sourceApp,
			fireInvitedAppShortcutsCacheRefreshSucceeded,
			fireInvitedAppShortcutsCacheRefreshFailed,
		],
	);

	const referrableShortcuts: ShortcutsConfig = useMemo(() => {
		const entries = Object.entries(shortcutsConfig);
		const max = Math.min(
			entries.length,
			Object.values(resolverResult.invitedApps ?? {}).filter((app) => app?.eligible).length,
		);
		const refsLimit = max - excludeLastFromRefs;
		let createdRefsCount = 0;

		return entries.reduce((acc, [key, shortcut]) => {
			if (
				createdRefsCount < refsLimit &&
				shortcut.type === 'link' &&
				shortcut.apsKey &&
				resolverResult.invitedApps?.[
					getAppKeyByApsKey(shortcut.apsKey as InvitedAppShortcutAllowedApsKey)
				]?.eligible
			) {
				const apsKey = shortcut.apsKey as InvitedAppShortcutAllowedApsKey;
				const targetRef = shortcutRefs[apsKey];
				acc[key] = {
					...shortcut,
					ref: targetRef ? setShortcutRef(targetRef) : undefined,
					labelElem: (
						<InvitedAppShortcutLabel
							apsKey={apsKey}
							label={shortcut.label}
							// Only this mode resolves a variant; the experiment inner passes none.
							variant={resolverResult.invitedApps?.[getAppKeyByApsKey(apsKey)]?.variant}
							onAnimatedImpressed={handleAnimatedImpressed}
						/>
					),
				};
				// oxlint-disable-next-line no-plusplus
				createdRefsCount++;
			} else {
				acc[key] = shortcut;
			}
			return acc;
		}, {} as ShortcutsConfig);
	}, [
		shortcutsConfig,
		resolverResult.invitedApps,
		excludeLastFromRefs,
		shortcutRefs,
		setShortcutRef,
		handleAnimatedImpressed,
	]);

	const currentApsKey = useMemo(() => {
		const entry = Object.entries(shortcutRefs).find(([, ref]) => ref === currentShortcutRef);
		return entry ? (entry[0] as InvitedAppShortcutAllowedApsKey) : undefined;
	}, [currentShortcutRef, shortcutRefs]);

	const currentApp: InvitedAppShortcutAllowedTargetAppKey | undefined = currentApsKey
		? getAppKeyByApsKey(currentApsKey)
		: undefined;

	const collaboratorsSource = useMemo<CollaboratorsSource | undefined>(
		() => (currentApp ? { kind: 'lazy', app: currentApp } : undefined),
		[currentApp],
	);

	const handleAcceptInvite = useCallback(
		(app: Parameters<UseInvitedAppShortcutsResult['registerActivation']>[0]) =>
			resolverResult?.registerActivation(app),
		[resolverResult],
	);

	const currentShortcutLink = useMemo(
		() => currentShortcutRef?.current?.href,
		[currentShortcutRef],
	);

	useEffect(
		() => () => {
			unbindFunctionsRef.current.forEach((unbind) => unbind());
			unbindFunctionsRef.current.clear();
			clearClosePopupTimer();
		},
		[clearClosePopupTimer],
	);

	useEffect(() => {
		// Collect target-app keys for shortcuts we attached a ref to. Mirrors
		// the Inner reducer pattern; analytics signature expects target-app
		// keys (e.g. `confluence` / `jira`) not aps keys.
		const invitedAppShortcutKeys: InvitedAppShortcutAllowedTargetAppKey[] = Object.values(
			referrableShortcuts,
		).reduce((acc, shortcut) => {
			if (shortcut.type === 'link' && isAllowedApsKey(shortcut.apsKey) && !!shortcut.ref) {
				acc.push(getAppKeyByApsKey(shortcut.apsKey));
			}
			return acc;
		}, [] as InvitedAppShortcutAllowedTargetAppKey[]);

		if (invitedAppShortcutKeys.length > 0) {
			// Productionisation mode: GRS resolves impressions, so the "first
			// impressed" list is always empty — pass `[]` to keep the analytics
			// signature intact.
			fireViewedInvitedAppShortcuts(invitedAppShortcutKeys, []);
		}
	}, [referrableShortcuts, fireViewedInvitedAppShortcuts]);

	// Productionisation mode: impression state is resolved by GRS from the TAP
	// first-impression trait and arrives as the lozenge `variant`. The frontend still
	// produces the signal that feeds the trait (the `animatedLozenge` event) — it just
	// no longer reads impression state. The productionisation hook never sets
	// `firstImpressionAt`, so the legacy `impressedProducts` flags are always `false`
	// here by construction.
	const contextValue = useMemo(
		() => ({
			impressedProducts: {
				JIRA_SOFTWARE: false,
				CONFLUENCE: false,
			},
		}),
		[],
	);

	return (
		<InvitedAppShortcutsContext.Provider value={contextValue}>
			{children({ referrableShortcuts })}
			{isPopupShown &&
				currentShortcutRef &&
				currentShortcutLink &&
				currentApsKey &&
				collaboratorsSource && (
					<LazyInvitedAppPopup
						trigger={currentShortcutRef}
						onMouseEnterPopup={handleOnMouseEnterPopup}
						onMouseLeavePopup={handleOnMouseLeavePopup}
						currentApsKey={currentApsKey}
						collaboratorsSource={collaboratorsSource}
						currentShortcutLink={currentShortcutLink}
						onAcceptInvite={handleAcceptInvite}
					/>
				)}
		</InvitedAppShortcutsContext.Provider>
	);
};
